/**
 * Google Analytics 4 (GA4) + Google Ads Tracking Utilities
 *
 * Event tracking helpers for GA4 and Google Ads conversions.
 * Same measurement ID as old VidSeeds.ai for domain continuity.
 *
 * Privacy: Do not send PII (emails, names, IDs, tokens).
 * GA4 Measurement ID: G-DTQ6JYMKV3 (override via NEXT_PUBLIC_GA_MEASUREMENT_ID)
 * Google Ads ID: AW-17743054181 (override via NEXT_PUBLIC_GOOGLE_ADS_ID)
 */

import { createErrorReporter } from "@/lib/observability/report-error";

const report = createErrorReporter("lib.analytics.gtag", "analytics");

type GaConsentUpdate = Partial<{
  ad_storage: "granted" | "denied";
  ad_user_data: "granted" | "denied";
  ad_personalization: "granted" | "denied";
  analytics_storage: "granted" | "denied";
}>;

declare global {
  interface Window {
    gtag?: {
      (command: "set", params: Record<string, unknown>): void;
      (
        command: string,
        eventNameOrConfig: string,
        params?: Record<string, unknown>
      ): void;
    };
    dataLayer?: unknown[];
    vidSeedsConsent?: {
      update: (options?: GaConsentUpdate) => void;
      grantAll: () => void;
      denyAll: () => void;
    };
  }
}

const DEFAULT_GA4_MEASUREMENT_ID = "G-DTQ6JYMKV3";

function getGa4MeasurementId(): string {
  const fromEnv = process.env.NEXT_PUBLIC_GA_MEASUREMENT_ID;
  if (typeof fromEnv === "string" && fromEnv.trim()) {
    return fromEnv.trim();
  }
  return DEFAULT_GA4_MEASUREMENT_ID;
}

function getGoogleAdsId(): string | null {
  const fromEnv = process.env.NEXT_PUBLIC_GOOGLE_ADS_ID;
  if (typeof fromEnv === "string" && fromEnv.trim()) {
    return fromEnv.trim();
  }
  return null;
}

function isGtagAvailable(): boolean {
  return typeof window !== "undefined" && typeof window.gtag === "function";
}

type AnalyticsParamValue = string | number | boolean;

const DISALLOWED_PARAM_KEY =
  /(^|_)(email|e-mail|user_id|userid|name|full_name|first_name|last_name|phone|address|token|jwt|session)(_|$)/i;
const EMAIL_LIKE_VALUE = /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/i;
function sanitizeParams(
  params: Record<string, unknown>
): Record<string, AnalyticsParamValue> {
  const sanitized: Record<string, AnalyticsParamValue> = {};

  for (const [key, rawValue] of Object.entries(params)) {
    if (!key || DISALLOWED_PARAM_KEY.test(key)) continue;
    if (rawValue === undefined || rawValue === null) continue;

    if (typeof rawValue === "string") {
      const value = rawValue.trim();
      if (!value || EMAIL_LIKE_VALUE.test(value)) continue;
      sanitized[key] = value.length > 120 ? value.slice(0, 120) : value;
      continue;
    }
    if (typeof rawValue === "number") {
      if (!Number.isFinite(rawValue)) continue;
      sanitized[key] = rawValue;
      continue;
    }
    if (typeof rawValue === "boolean") {
      sanitized[key] = rawValue;
      continue;
    }
  }
  return sanitized;
}

/** Base event tracking. All other functions use this internally. */
export function trackEvent(
  eventName: string,
  params: Record<string, unknown> = {}
): void {
  if (!isGtagAvailable()) {
    if (process.env.NODE_ENV === "development") {
      console.log("[Analytics]", eventName, params);
    }
    return;
  }

  try {
    window.gtag?.("event", eventName, sanitizeParams(params));
  } catch (error) {
    console.error("[Analytics] Failed to track event:", eventName, error);
    report(error, {
      action: "trackEvent",
      severity: "warning",
      extra: { eventName },
    });
  }
}

/** Track page views for SPA route transitions. */
export function trackPageView(
  pagePath: string,
  pageTitle: string,
  additionalData?: Record<string, unknown>
): void {
  if (!isGtagAvailable()) {
    if (process.env.NODE_ENV === "development") {
      console.log("[Analytics] Page view:", pagePath, pageTitle);
    }
    return;
  }

  try {
    let pageLocation: string | undefined;
    if (typeof window !== "undefined") {
      try {
        const resolved = new URL(pagePath, window.location.origin);
        pageLocation = resolved.origin + resolved.pathname;
      } catch (error) {
        console.error(
          "[observability] Caught error in lib/analytics/gtag.ts",
          error
        );
        report(error, {
          action: "trackPageView.resolveUrl",
          severity: "warning",
          extra: { pagePath },
        });
        pageLocation = undefined;
      }
    }

    window.gtag?.("config", getGa4MeasurementId(), {
      page_path: pagePath,
      page_location: pageLocation,
      page_title: pageTitle,
      ...additionalData,
      send_page_view: true,
    });
  } catch (error) {
    console.error("[Analytics] Failed to track page view:", error);
    report(error, {
      action: "trackPageView",
      severity: "warning",
      extra: { pagePath, pageTitle },
    });
  }
}

/** Track signup (GA4 recommended event). */
export function trackSignUp(method: string): void {
  trackEvent("sign_up", { method });
}

/** Track login (GA4 recommended event). */
export function trackLogin(method: string): void {
  trackEvent("login", { method });
}

/** Track CTA clicks. */
export function trackCTAClick(
  ctaType: string,
  page: string,
  destination: string,
  ctaText?: string
): void {
  trackEvent("cta_click", {
    cta_type: ctaType,
    page,
    destination,
    cta_text: ctaText,
  });
}

/** Track conversions (signups, subscriptions, purchases). */
export function trackConversion(
  eventName: string,
  value?: number,
  currency = "USD",
  additionalData?: Record<string, unknown>
): void {
  trackEvent(eventName, { value, currency, ...additionalData });
}

// ---------------------------------------------------------------------------
// Google Ads Conversion Tracking
// ---------------------------------------------------------------------------

/**
 * Fire a Google Ads conversion event.
 *
 * @param label  The conversion label from Google Ads (e.g. "A70LCJ_l9JUcEOWKxoxC")
 * @param value  Optional conversion value in USD
 */
function trackGoogleAdsConversion(
  label: string,
  value?: number,
  currency = "USD"
): void {
  const adsId = getGoogleAdsId();
  if (!adsId || !isGtagAvailable()) {
    if (process.env.NODE_ENV === "development") {
      console.log("[Analytics] Google Ads conversion:", label, { value });
    }
    return;
  }

  try {
    const params: Record<string, unknown> = {
      send_to: `${adsId}/${label}`,
    };
    if (value !== undefined && Number.isFinite(value)) {
      params.value = value;
      params.currency = currency;
    }
    window.gtag?.("event", "conversion", params);
  } catch (error) {
    console.error("[Analytics] Google Ads conversion failed:", label, error);
    report(error, {
      action: "trackGoogleAdsConversion",
      severity: "warning",
      extra: { label },
    });
  }
}

/**
 * Track account creation conversion for Google Ads.
 * Called from ConversionTracker when a new user lands on the dashboard.
 */
export function trackSignUpConversion(): void {
  const label = process.env.NEXT_PUBLIC_GADS_SIGNUP_LABEL;
  if (!label) return;

  // GA4 recommended event
  trackSignUp("oauth");

  // Google Ads conversion
  trackGoogleAdsConversion(label);
}

/**
 * Track channel/platform connection conversion for Google Ads.
 * Called from AccountsManager when ?connected=platform is detected.
 */
export function trackChannelConnectedConversion(platform: string): void {
  const label = process.env.NEXT_PUBLIC_GADS_CHANNEL_LABEL;
  if (!label) return;

  // GA4 custom event
  trackEvent("channel_connected", { platform });

  // Google Ads conversion
  trackGoogleAdsConversion(label);
}

/**
 * Track purchase/subscription conversion for Google Ads.
 * Called from useSeedsStore when ?purchase=success or ?subscription=success.
 */
export function trackPurchaseConversion(
  value?: number,
  currency = "USD"
): void {
  const label = process.env.NEXT_PUBLIC_GADS_PURCHASE_LABEL;
  if (!label) return;

  // GA4 recommended event
  trackEvent("purchase", { value, currency });

  // Google Ads conversion
  trackGoogleAdsConversion(label, value, currency);
}
